import { useEffect, useRef, useState } from 'react'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import cn from 'classnames'
import { Input } from 'components/Input'
import { Button, BUTTON_TYPES } from 'components/Button'
import { BTN_TXT, LABELS, PLACEHOLDERS } from 'constants/txt'
import { validateMessages } from 'helper/common'
import {
  TCreateGroupChatProps,
  TCreateGroupChatForm,
  TParams
} from './forms.createGroupChat.d'
import './forms.createGroupChat.scss'
import { Chips } from 'components/Chips/Chips'
import { useDebounce } from 'hooks/hooks'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import IconNameItem from 'components/IconNameItem/IconNameItem'
import { CHAT_TYPE, ROLES_NAMES } from 'constants/common'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import 'components/Checkbox/checkbox.default.scss'
import { Checkbox } from '../Checkbox'
import { IPartialUserInfo } from '../../redux/store/user/types'
import { NoUsersState } from 'features/ChatList/Sidebar/NoUsersState'
import { CheckboxValueType } from 'antd/es/checkbox/Group'

export const CreateGroupChat = ({
  className,
  onSubmit,
  onCancel,
  getAvailableUsers
}: TCreateGroupChatProps): JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [users, setUsers] = useState<IPartialUserInfo[]>([])
  const [selectedUsersIds, setSelectedUsersIds] = useState<CheckboxValueType[]>(
    []
  )
  const [selectedUsers, setSelectedUsers] = useState<IPartialUserInfo[]>([])
  const [form] = Form.useForm()
  const debouncedSearch = useDebounce(searchValue, 500)
  const dispatch = useDispatch()

  const getUsers = () => {
    dispatch(setLoading(true))
    const params: TParams = { search: searchValue }
    if (searchValue) {
      params.search = searchValue
    }
    getAvailableUsers(params)
      .then((resp) => setUsers(resp?.data?.results))
      .finally(() => dispatch(setLoading(false)))
  }

  const onSubmitForm = (values) => {
    onSubmit({
      ...values,
      ...{ members: selectedUsersIds, type: CHAT_TYPE.GROUP }
    })
  }

  useEffect(() => {
    getUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const onDeleteUser = (uuid: string) => {
    setSelectedUsersIds(
      selectedUsersIds.filter((itemUuid) => itemUuid !== uuid)
    )
    setSelectedUsers(selectedUsers.filter((item) => item.uuid !== uuid))
  }

  const onFinishFailed = () => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleChangeSelected = (selected: CheckboxValueType[]) => {
    if (searchValue) {
      const filteredUserIds: CheckboxValueType[] = selectedUsersIds.filter(
        (id) => !users.find((user) => user.uuid === id)
      )

      const filteredUsers: IPartialUserInfo[] = selectedUsers.filter(
        (user) => !users.find((item) => item.uuid === user.uuid)
      )

      const newSelectedUsers = users.filter((item) =>
        selected.includes(item.uuid)
      )

      const updatedUserIds: CheckboxValueType[] = Array.from(
        new Set([...filteredUserIds, ...selected])
      )
      const updatedUsers: IPartialUserInfo[] = Array.from(
        new Set([...filteredUsers, ...newSelectedUsers])
      )

      setSelectedUsersIds(updatedUserIds)
      setSelectedUsers(updatedUsers)
    } else {
      setSelectedUsersIds(selected)
      const selectedUsersData = users.filter((item) =>
        selected.includes(item.uuid)
      )
      setSelectedUsers(selectedUsersData)
    }
  }

  return (
    <CustomForm<TCreateGroupChatForm>
      form={form}
      className={cn('create-group-chat_form', className)}
      layout="vertical"
      onFinish={onSubmitForm}
      onFinishFailed={onFinishFailed}
      validateMessages={validateMessages}
    >
      <div ref={ref}>
        <Input
          propsItem={{
            label: LABELS.CHAT_NAME,
            name: 'name',
            rules: [
              {
                max: 24,
                required: true
              }
            ]
          }}
          propsInput={{
            showCount: true,
            maxLength: 24
          }}
        />
      </div>
      <Input.TextArea
        propsItem={{
          label: LABELS.DESCRIPTION,
          name: 'description',
          rules: [
            {
              max: 250
            }
          ]
        }}
        propsTextArea={{
          showCount: true,
          maxLength: 250
        }}
      />
      <div className="create-group-chat_separator" />
      {!!selectedUsers?.length && (
        <div className="create-group-chat_selected-users">
          {selectedUsers.map((user) => {
            {
              return (
                <Chips
                  className="create-group-chat_chips"
                  closeIcon={<Close16 />}
                  closable
                  children={`${user?.first_name} ${user?.last_name}`}
                  uuid={user?.uuid}
                  key={user?.uuid}
                  onDelete={onDeleteUser}
                />
              )
            }
          })}
        </div>
      )}
      <Input
        propsInput={{
          placeholder: PLACEHOLDERS.SEARCH_USERS1,
          prefix: <Search16 />,
          value: searchValue,
          onChange: (e) => setSearchValue(e.target.value)
        }}
      />
      <div className="create-group-chat_users-list_container">
        {users.length === 0 ? (
          <NoUsersState />
        ) : (
          <Checkbox.Group
            className="create-group-chat_users-list"
            propsGroupCheckbox={{
              value: selectedUsersIds,
              onChange: handleChangeSelected,
              options: users.map((user) => ({
                value: user.uuid,
                label: (
                  <IconNameItem
                    firstName={user.first_name}
                    lastName={user.last_name}
                    src={user.avatar?.file_preview || null}
                    roleName={ROLES_NAMES[user.role]}
                    organization={user?.organization_name}
                    facilityName={user.facility_name}
                  />
                )
              }))
            }}
          />
        )}
      </div>
      <div className="create-group-chat_buttons">
        <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
          {BTN_TXT.CANCEL}
        </Button>
        <Button
          type={BUTTON_TYPES.PRIMARY}
          htmlType="submit"
          disabled={!selectedUsersIds.length}
          upperCase
        >
          {BTN_TXT.CREATE_CHAT}
        </Button>
      </div>
    </CustomForm>
  )
}
